import type { AuthResponse, LoginCredentials, RegisterCredentials, User } from '@/types/auth'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class AuthApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'AuthApiError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json()

  if (!response.ok) {
    throw new AuthApiError(response.status, data.error || 'Request failed')
  }

  return data
}

export const authApi = {
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentials),
    })
    return handleResponse<AuthResponse>(response)
  },

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(credentials),
    })
    return handleResponse<AuthResponse>(response)
  },

  async logout(accessToken: string): Promise<void> {
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
  },

  async refresh(): Promise<AuthResponse> {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    return handleResponse<AuthResponse>(response)
  },

  async getMe(accessToken: string): Promise<{ user: User }> {
    const response = await fetch(`${API_URL}/auth/me`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
    return handleResponse<{ user: User }>(response)
  },

  async verifyEmail(token: string): Promise<{ message: string; user: User | null }> {
    const response = await fetch(`${API_URL}/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    })
    return handleResponse<{ message: string; user: User | null }>(response)
  },

  async resendVerification(accessToken: string): Promise<{ message: string; verificationUrl?: string }> {
    const response = await fetch(`${API_URL}/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
    return handleResponse<{ message: string; verificationUrl?: string }>(response)
  },

  async deleteAccount(accessToken: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/auth/account`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
    return handleResponse<{ success: boolean; message: string }>(response)
  },

  async exportData(accessToken: string): Promise<Blob> {
    const response = await fetch(`${API_URL}/auth/export`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const data = await response.json()
      throw new AuthApiError(response.status, data.error || 'Failed to export data')
    }

    return response.blob()
  },

  async updateProfile(accessToken: string, data: { name?: string }): Promise<{ user: User }> {
    const response = await fetch(`${API_URL}/auth/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })
    return handleResponse<{ user: User }>(response)
  },

  async changePassword(
    accessToken: string,
    data: { currentPassword: string; newPassword: string }
  ): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/auth/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })
    return handleResponse<{ message: string }>(response)
  },
}

export { AuthApiError }
